import React, { useState } from 'react';
import EDTableRow from '../../../../../../../../../BaseComponents/Table/Components/EDTableRow';
import { findFormatter } from '../../../../../../../../../Utils/Formatters/FindFormatter';
import {
  IRadBaseradAnalys,
  IRow,
} from '../../../../Queries/EkonomiskAnalysRadBaseradQuery/EkonomiskAnalysRadBaseradQuery';
import { getCurrencyFormatterParams } from '../../../../Utils/paramUtils';
import EkonomiskAnalysRadBaseradGranskning from '../../../EkonomiskAnalysRadBaseradGranskning/EkonomiskAnalysRadBaseradGranskning';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import TableCell from '../../../EkonomiskAnalysTableCell/EkonomiskAnalysTableCell';
import {
  firstVisibleIndex,
  getPercentColumnIndex,
} from '../../../EkonomiskAnalysRadBaseradTable';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../../../EkonomiskAnalysMain/EkonomiskAnalysMain';
import EDTableCell from '../../../../../../../../../BaseComponents/Table/Components/EDTableCell';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../../../EkonomiskAnalysPage';

interface IProps {
  analys: IRadBaseradAnalys;
  analysSettings: IFilledLocalAnalys;
  examineIndex: number | undefined;
  impersonatedCodePrivilege: ISelectedAnalysCodePrivilege | undefined;
  updatedAnalysSettings: ILocalAnalys;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege | undefined
  ) => void;
  row: IRow;
  rowIndex: number;
  selectedAnalys: ISelectedAnalys;
  showTableInfo: (
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>
  ) => void;
  toggleExamine: (rowIndex: number) => void;
  valueColumnIndexes: number[];
}

const useStyles = makeStyles()((theme: Theme) => ({
  selectedRowBG: {
    backgroundColor:
      theme.customProperties.EkonomiskAnalysRowColors.selectedRowBoxBGColor,
    '&hover': {
      backgroundColor:
        theme.customProperties.EkonomiskAnalysRowColors.selectedRowBoxBGColor,
    },
  },
  borderlessRow: {
    borderBottom: 0,
  },
  noValueRow: {
    height: theme.spacing(1.25),
  },
  granskningRow: {
    paddingTop: 0,
    paddingBottom: theme.spacing(3),
  },
  granskningScrollContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
  tableCell: {
    position: 'relative',
    '& .colPaint': {
      display: 'none',
      position: 'absolute',
      left: 0,
      width: '100%',
      backgroundColor: theme.customProperties.Fade.BlueGray.selectedLightFade,
      mixBlendMode: 'multiply',
      zIndex: 0,
      pointerEvents: 'none',
    },
    '&:hover .colPaint': {
      display: 'block',
    },
  },
  notCell: {
    width: 75,
  },
  table: {
    tableLayout: 'auto',
  },
  lastColumnCell: {
    padding: 0,
  },
  firstColumnCell: {
    maxWidth: '375px',
  },
}));

const DefaultRowType: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes, cx } = useStyles();
  const analysSettings = props.analysSettings;
  const row = props.row;
  const rowIndex = props.rowIndex;
  const valueColumnIndexes = props.valueColumnIndexes;
  const [hoverRowIndex, setHoverRowIndex] = useState<number>();

  const padding = 8 + row.level * 16;
  const emptyValues =
    valueColumnIndexes.length > 0 &&
    valueColumnIndexes.every(x => Number(row.values[x]) === 0);
  const canBeExamined =
    row.rowType === 'B' && row.urvalId !== null && !emptyValues;
  const examineOpen = props.examineIndex === rowIndex;

  return (
    <React.Fragment key={props.row.dimRowID + props.rowIndex}>
      <EDTableRow
        hover={canBeExamined && !examineOpen}
        lightBackground={row.level >= 3 && row.hasData && !emptyValues}
        className={cx({
          [classes.selectedRowBG]: examineOpen,
          [classes.noValueRow]: !row.hasData,
        })}
        onMouseEnter={() => setHoverRowIndex(rowIndex)}
        onMouseLeave={() => setHoverRowIndex(-1)}
      >
        {row.values.map((val, colIndex) => {
          if (!props.analys.columns[colIndex].visible) {
            return null;
          }

          const firstVisibleColumnIndex = firstVisibleIndex(
            props.analys.columns
          );

          const procentColumnIndex = getPercentColumnIndex(
            props.analys.columns
          );

          const isFirst = colIndex === firstVisibleColumnIndex;

          const isNot = props.analys.columns[colIndex].fieldType === 'radnot';

          const isInverted = props.analys.columns[colIndex].inverted;

          const isPercentRow =
            procentColumnIndex >= 0 &&
            props.analys.rows[rowIndex].values[procentColumnIndex];

          const useFormatter = !isFirst && !isNot;

          const formatter =
            isPercentRow || props.analys.columns[colIndex].isPercent
              ? findFormatter('TEDFormatIntAsPercent')
              : findFormatter('TEDFormatCurr');

          const reverseString = isInverted ? 'ReverseSign="Y";' : '';

          const formatterParams =
            isPercentRow || props.analys.columns[colIndex].isPercent
              ? reverseString + 'Decimals="1";DisplayZeroAsBlank="Y"'
              : getCurrencyFormatterParams(
                  props.analys.columns[colIndex],
                  props.updatedAnalysSettings.other.denomination,
                  props.updatedAnalysSettings.other.decimals
                );

          val =
            isPercentRow && useFormatter
              ? (
                  Number(val) /
                  (analysSettings.defaultSettings.denomination * 100)
                ).toString()
              : val;

          return (
            <TableCell
              key={props.analys.columns[colIndex].title1 + colIndex}
              alignment={isFirst ? 'left' : 'right'}
              paddingLeft={isFirst ? padding : undefined}
              formatter={useFormatter ? formatter : undefined}
              formatterParams={useFormatter ? formatterParams : undefined}
              value={val}
              textVariant={row.level < 3 || isFirst ? 'subtitle2' : 'body2'}
              onClick={
                canBeExamined ? () => props.toggleExamine(rowIndex) : undefined
              }
              pointer={canBeExamined}
              classes={{
                cell: examineOpen ? classes.borderlessRow : undefined,
              }}
              onMouseOver={e => props.showTableInfo(e)}
              className={cx({
                [classes.tableCell]: !isFirst,
                [classes.notCell]: isNot,
                [classes.firstColumnCell]: isFirst,
                [classes.noValueRow]: !row.hasData,
              })}
              disableNoWrap={isFirst}
              clickableObjectColorMarking={colIndex === 4 && canBeExamined}
              hovering={
                colIndex === 4 && hoverRowIndex === rowIndex && canBeExamined
              }
              extraBold={row.bold}
            />
          );
        })}
        <EDTableCell
          className={cx(classes.lastColumnCell, {
            [classes.borderlessRow]: examineOpen,
          })}
        />
      </EDTableRow>
      {examineOpen && props.examineIndex !== undefined && (
        <EDTableRow>
          <EDTableCell
            colSpan={props.analys.columns.filter(x => x.visible).length + 2}
            className={cx(classes.selectedRowBG, classes.granskningRow)}
          >
            <div className={classes.granskningScrollContainer}>
              <EkonomiskAnalysRadBaseradGranskning
                settings={props.analysSettings}
                updatedSettings={props.updatedAnalysSettings}
                urvalId={props.analys.rows[props.examineIndex].urvalId || ''}
                selectedAnalys={props.selectedAnalys}
                impersonatedCodePrivilege={props.impersonatedCodePrivilege}
                updateImpersonatedCodePrivilege={
                  props.updateImpersonatedCodePrivilege
                }
                rowName={
                  props.analys.rows[rowIndex].values[
                    firstVisibleIndex(props.analys.columns)
                  ]
                }
              />
            </div>
          </EDTableCell>
        </EDTableRow>
      )}
    </React.Fragment>
  );
};

export default DefaultRowType;
